import type { IYadavDiamonds } from '@framework/types'
import { shapes } from '@utils/diamond-attributes'
import axios from 'axios'
import { IDiamondFilter } from 'types/diamondFilter.types'

export const fetchDiamonds = async (state: IDiamondFilter) => {
  const diamondParams = normalizeDiamondFilterParams(state)

  try {
    const response = await axios<IYadavDiamonds>(
      `${process.env.apiUrl}?${diamondParams}`
    )
    const { data } = response
    return data
  } catch (error) {
    console.error(
      'An error ocurred when fetching diamonds. Please try again later.'
    )
    return null
  }
}

function normalizeDiamondFilterParams(data: IDiamondFilter) {
  const cut = {
    Poor: 0,
    Fair: 1,
    Good: 2,
    'Very Good': 3,
    Excellent: 4,
    'Signature Ideal': 5,
    'Super Ideal': 6,
  }

  const color = {
    Fancy: 0,
    YELLOW: 1,
    PINK: 2,
    BLUE: 3,
    ORANGE: 4,
    GREY: 5,
    BROWN: 6,
    GREEN: 7,
    P: 11,
    O: 12,
    N: 13,
    M: 14,
    L: 15,
    K: 16,
    J: 17,
    I: 18,
    H: 19,
    G: 20,
    F: 21,
    E: 22,
    D: 23,
  }

  const clarity = {
    I3: 0,
    I2: 1,
    I1: 2,
    SI3: 3,
    SI2: 4,
    SI1: 5,
    VS2: 6,
    VS1: 7,
    VVS2: 8,
    VVS1: 9,
    IF: 10,
    FL: 11,
  }

  const polsymm = {
    Poor: 0,
    Fair: 1,
    Good: 2,
    'Very Good': 3,
    Excellent: 4,
    Ideal: 5,
  }

  const fluor = {
    'Very Strong': 0,
    Strong: 1,
    Medium: 2,
    Faint: 3,
    Slight: 4, // Deprecated values. See ticket #106110
    'Very Slight': 5, // Deprecated values. See ticket #106110
    None: 6,
  }

  // If the FluorescenceCheckboxFilter has no checkboxes active, send an empty array for lab diamonds.
  // For natural diamonds send an array from 1 to 6 excluding 4 and 5 (Slight and Very Slight)
  const fluorParams =
    data.selectiveFluorescenceValues.length === 0
      ? data.labCreated
        ? [].toString()
        : [1, 2, 3, 6].toString()
      : data.selectiveFluorescenceValues.toString()

  const polishParams = data.selectivePolishValues.toString()

  const symmetryParams = data.selectiveSymmetryValues.toString()

  const queryParams = new URLSearchParams({
    shape: `${data.shape}`,
    price_min: `${data.minPrice}`,
    price_max: `${data.maxPrice}`,
    weight_min: `${
      data.minCarat === '.' ? (data.labCreated ? '0.7' : '0.2') : data.minCarat
    }`,
    weight_max: `${data.maxCarat === '.' ? '20.0' : data.maxCarat}`,
    cut_min: '4',
    cut_max: '4',
    color_min: `${color[data.activeColorLabels[0] as keyof typeof color]}`,
    color_max: `${
      color[data.activeColorLabels.slice(-1)[0] as keyof typeof color]
    }`,
    clarity_min: `${
      clarity[data.activeClarityLabels[0] as keyof typeof clarity]
    }`,
    clarity_max: `${
      clarity[data.activeClarityLabels.slice(-1)[0] as keyof typeof clarity]
    }`,
    polish: polishParams,
    symmetry: symmetryParams,
    fluor: fluorParams,
    depth_min: `${data.minDepth === '.' ? '43' : data.minDepth}`,
    depth_max: `${data.maxDepth === '.' ? '88' : data.maxDepth}`,
    tablep_min: `${data.minTable}`,
    tablep_max: `${data.maxTable}`,
    ratio_min: `${data.shape.toUpperCase() === 'RB' ? '1' : data.minRatio}`,
    ratio_max: `${data.shape.toUpperCase() === 'RB' ? '2.5' : data.maxRatio}`,
    with_gia: `${data.giaCert}`,
    with_gcal: `${data.labCreated ? data.gcalCert : false}`,
    with_igi: `${data.labCreated ? data.igiCert : false}`,
    with_image: `${data.withImg}`,
    quick_ship: `${data.quickShip}`,
    labcreated: `${data.labCreated}`,
    sort: `${data.sort ? data.sort : ''}`,
    direction: `${data.direction ? data.direction : ''}`,
    page: `${data.page ? data.page : 1}`,
    per_page: `${data.perPage ? data.perPage : 20}`,
    with_ftc_link: `${data.withFtcLink ? 'true' : ''}`,
  })
  return queryParams.toString()
}

export const generateFilterSlug = (state: IDiamondFilter) => {
  const minCarat = state.minCarat === '.' ? '0.2' : state.minCarat
  const maxCarat = state.maxCarat === '.' ? '20.0' : state.maxCarat
  const carat = `${Number(minCarat)}-${Number(maxCarat)}-carat`
  const shape = `${shapes[state.shape as keyof typeof shapes]
    .toLowerCase()
    .replace(' ', '_')}-diamonds`

  const minCut = 'excellent'
  const maxCut = 'excellent'
  const cut = minCut === maxCut ? `${minCut}-cut` : `${minCut}-${maxCut}-cut`

  const minColor = `${state.activeColorLabels.slice(0, 1)[0].toLowerCase()}`
  const maxColor = `${state.activeColorLabels.slice(-1)[0].toLowerCase()}`
  const color =
    minColor === maxColor
      ? `${minColor}-color`
      : `${minColor}-${maxColor}-color`

  const minClarity = `${state.activeClarityLabels.slice(0, 1)[0].toLowerCase()}`
  const maxClarity = `${state.activeClarityLabels.slice(-1)[0].toLowerCase()}`
  const clarity =
    minClarity === maxClarity
      ? `${minClarity}-clarity`
      : `${minClarity}-${maxClarity}-clarity`

  return `${carat}/${shape}/${cut}/${color}/${clarity}`
}

export const generateSEOTitle = (state: IDiamondFilter) => {
  const minCarat = state.minCarat === '.' ? '0.2' : state.minCarat
  const maxCarat = state.maxCarat === '.' ? '20.0' : state.maxCarat
  const diamondType = state.labCreated ? 'Lab Grown' : 'Natural'

  return `Search ${diamondType} ${
    shapes[state.shape as keyof typeof shapes]
  } Cut Diamonds | Yadav Diamonds and Jewelry`
}

export const generateSEODescription = (state: IDiamondFilter) => {
  let shapeDescription = ''

  if (state.labCreated) {
    switch (shapes[state.shape as keyof typeof shapes]) {
      case 'Round':
        shapeDescription =
          "Yadav Jewelry's round-cut lab-created diamonds are a classic choice, offering maximum brilliance and fire. Perfect for engagement rings, earrings, and more, these diamonds are a timeless symbol of elegance."
        break
      case 'Cushion':
        shapeDescription =
          'Yadav Jewelry offers stunning cushion-cut lab-created diamonds, combining brilliance and a soft, rounded-square shape. Explore these diamonds for a vintage-inspired, romantic look.'
        break
      case 'Oval':
        shapeDescription =
          'Yadav Jewelry’s collection of oval-cut lab-created diamonds offers a blend of modern elegance and timeless sparkle. The elongated shape creates a larger visual size, ideal for engagement rings.'
        break
      case 'Pear':
        shapeDescription =
          'Pear-cut lab-created diamonds, available at Yadav Jewelry, are known for their unique teardrop shape, offering a blend of classic and contemporary appeal in any setting.'
        break
      case 'Princess':
        shapeDescription =
          'Browse Yadav Jewelry’s collection of princess-cut lab-created diamonds. These square-shaped diamonds offer sharp, geometric beauty and intense brilliance, making them a popular choice for modern jewelry.'
        break
      case 'Asscher':
        shapeDescription =
          "Browse Yadav Jewelry's selection of Asscher-cut lab-created diamonds. Known for their elegant step cuts, these diamonds make a bold, timeless statement in any jewelry setting."
        break
      case 'Heart':
        shapeDescription =
          'Fall in love with heart-cut lab-created diamonds from Yadav Jewelry. These romantic, symbol-shaped diamonds are a unique and thoughtful choice for engagement rings and fine jewelry.'
        break
      case 'Emerald':
        shapeDescription =
          'Discover the elegance of emerald-cut lab-created diamonds at Yadav Jewelry. These diamonds feature clean lines and a sophisticated rectangular shape, perfect for a modern, refined aesthetic.'
        break
      case 'Radiant':
        shapeDescription =
          'Explore Yadav Jewelry’s radiant-cut lab-created diamonds, combining the brilliance of round cuts with the elegant shape of emerald cuts. These diamonds are perfect for creating dazzling, unique jewelry pieces.'
        break
      case 'Square':
        shapeDescription =
          'Shop lab-created square radiant diamonds at Yadav Jewelry, offering stunning brilliance, eco-friendly sustainability, and exceptional value for your engagement ring.'
        break
      case 'Marquise':
        shapeDescription =
          'Discover the elongated beauty of marquise-cut lab-created diamonds at Yadav Jewelry. Their distinctive shape creates the illusion of longer, more slender fingers, making them a striking choice for engagement rings.'
        break
      default:
        shapeDescription = "Shop Yadav Jewelry's collection of lab-created diamonds. These eco-friendly, ethically sourced diamonds offer the same sparkle as natural diamonds at a more affordable price."
        break
    }
  } else {
    switch (shapes[state.shape as keyof typeof shapes]) {
      case 'Round':
        shapeDescription =
          'Yadav Jewelry’s round-cut loose diamonds are the epitome of classic elegance. Offering maximum brilliance and timeless appeal, these diamonds are perfect for any jewelry setting.'
        break
      case 'Cushion':
        shapeDescription =
          'Cushion-cut loose diamonds from Yadav Jewelry combine vintage charm with modern brilliance. The soft, rounded edges of this shape make it a popular choice for engagement rings and custom designs.'
        break
      case 'Oval':
        shapeDescription =
          'Oval-cut loose diamonds at Yadav Jewelry offer a blend of brilliance and modern elegance. These diamonds are ideal for engagement rings, offering a flattering, elongated appearance.'
        break
      case 'Pear':
        shapeDescription =
          'Pear-cut loose diamonds at Yadav Jewelry feature a distinctive teardrop shape, offering a blend of classic beauty and modern style. Perfect for engagement rings and unique custom designs.'
        break
      case 'Princess':
        shapeDescription =
          'Yadav Jewelry’s princess-cut loose diamonds feature sharp, square shapes with stunning brilliance. These diamonds are ideal for those seeking a modern, geometric engagement ring design.'
        break
      case 'Asscher':
        shapeDescription =
          'Experience the timeless sophistication of natural Asscher cut diamonds. Explore their mesmerizing step-cut facets and elegant allure. Find your perfect Asscher cut diamond for a truly exceptional piece.'
        break
      case 'Heart':
        shapeDescription =
          'Find heart-cut loose diamonds at Yadav Jewelry, perfect for romantic jewelry designs. These diamonds make a meaningful choice for engagement rings and other fine jewelry pieces.'
        break
      case 'Emerald':
        shapeDescription =
          'Explore emerald-cut loose diamonds at Yadav Jewelry, renowned for their sophisticated, clean lines and rectangular shape. Perfect for creating a timeless and elegant engagement ring.'
        break
      case 'Radiant':
        shapeDescription =
          'Shop radiant-cut loose diamonds at Yadav Jewelry, a perfect blend of brilliance and unique design. These diamonds are ideal for creating engagement rings that stand out with their dazzling sparkle.'
        break
      case 'Square':
        shapeDescription =
          'Discover the exquisite beauty of natural square radiant cut diamonds. Explore their striking blend of square shape and brilliant sparkle. Find your perfect square radiant cut diamond for a truly exceptional piece'
        break
      case 'Marquise':
        shapeDescription =
          'Browse marquise-cut loose diamonds at Yadav Jewelry, known for their elongated, pointed shape. These diamonds are perfect for those seeking a unique and sophisticated engagement ring or jewelry design.'
        break
      default:
        shapeDescription =
          'Shop Yadav Jewelry’s collection of loose diamonds, featuring various shapes, sizes, and qualities. Perfect for customizing your engagement ring or creating your own unique piece of fine jewelry.'
        break
    }
  }

  return shapeDescription
}
